import { getUserAnamneses, getUserPersonas } from "../services/AnamnesisService";
import { ensureAnamneseConfig, isAnamneseV2 } from "./anamneseVersion";
import { persistAnamneseEvaluation } from "./anamneseEvaluationStore";
import { listPersonaSubmissions, getSubmission } from "../anamnese-v2/api";

export function isAdultOrigin(origin) {
  const o = String(origin || "").toLowerCase();
  return !o.includes("baby") && !o.includes("kids");
}

// Só interessam personas adultas que já têm anamnese respondida.
export function buildPersonasWithAnamnese(personas, anamneses) {
  const anamMap = {};
  for (const a of anamneses) {
    const pid = a.persona_id;
    if (!pid) continue;
    if (!anamMap[pid]) anamMap[pid] = a;
  }

  return (Array.isArray(personas) ? personas : [])
    .filter((p) => isAdultOrigin(p.origin))
    .map((p) => ({ personaId: p.per_id, displayName: p.name, anamnese: anamMap[p.per_id] || null }))
    .filter((item) => item.anamnese);
}

/**
 * Personas adultas da conta que já têm anamnese, prontas para ir direto às soluções.
 * Na v2 basta /users/personas: cada persona traz o anm_id e a origem, e só vale a de
 * origin "anamnese_v2". As legadas ficam fora, porque /solucoes-purissima exige a avaliação
 * v2 e a pessoa as refaz pelo painel. A data de criação da persona não é a da anamnese,
 * então esses itens não a levam.
 * Espera o config da API: com o default (v1) a lista legada apareceria antes da hora.
 */
export async function loadAdultPersonasWithAnamnese() {
  const config = await ensureAnamneseConfig();

  if (!isAnamneseV2(config)) {
    const [personas, anamneses] = await Promise.all([getUserPersonas(), getUserAnamneses()]);
    return buildPersonasWithAnamnese(personas, anamneses);
  }

  const personas = await getUserPersonas();
  return (Array.isArray(personas) ? personas : [])
    .filter((p) => {
      const anm = p.anamnese || null;
      const origin = anm?.origin || p.origin;
      const anmId = anm?.anm_id || p.anm_id;
      return isAdultOrigin(origin) && origin === "anamnese_v2" && anmId && p.profile_uuid;
    })
    .map((p) => {
      const anm = p.anamnese || null;
      const anmId = anm?.anm_id || p.anm_id;
      return {
        personaId: p.per_id,
        displayName: p.name,
        anamnese: { id: anmId, profileUuid: p.profile_uuid, v2: true },
      };
    });
}

async function latestCompletedSubmissionUuid(profileUuid) {
  const list = await listPersonaSubmissions(profileUuid);
  const current = (Array.isArray(list) ? list : [])
    .filter((row) => row.status === "completed")
    .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))[0];
  return current?.uuid ?? null;
}

/** Deixa no storage a anamnese escolhida, como a /solucoes-purissima espera encontrá-la. */
export async function persistExistingAnamnese(item) {
  try {
    if (item.anamnese?.v2) {
      const uuid =
        item.anamnese?.submissionUuid ||
        (await latestCompletedSubmissionUuid(item.anamnese.profileUuid));
      const detail = uuid ? await getSubmission(uuid) : null;
      persistAnamneseEvaluation({
        evaluation: detail?.evaluation,
        submissionUuid: uuid,
        anmId: item.anamnese.id,
        personaId: item.personaId ?? null,
      });
    } else {
      localStorage.setItem("userAnswers", JSON.stringify(item.anamnese?.answers || {}));
      localStorage.setItem(
        "currentAnamnesis",
        JSON.stringify({ id: item.anamnese?.id, created_at: item.anamnese?.created_at })
      );
    }
  } catch {
    // noop
  }
}
